import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Card, Button, Form, Alert, Spinner, Image, Row, Col, Badge } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faImage, faTrash, faSave, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../api/api';
import { Routes } from '../../../routes';

const RepresentacionGraficaPage = () => {
    const navigate = useHistory();
    const ciemId = useSelector(state => state.ciem.ciemId);

    const [product, setProduct] = useState(null);
    const [savedBlobUrl, setSavedBlobUrl] = useState(null);  // URL de la portada guardada
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);   // blob URL del archivo recién seleccionado
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!ciemId) {
            alert('Para ingresar a esta opción debe seleccionar una idea de negocio');
            navigate.push(Routes.ListCiemPage.path);
        }
    }, [ciemId]);

    // Cargar producto y luego su portada desde GET /ciem/portada/{code}
    useEffect(() => {
        if (!ciemId) return;
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(`/ciem/${ciemId}`);
                const data = response.data.data;
                setProduct(data);

                // Cargar imagen de portada usando el code del producto
                if (data?.code) {
                    try {
                        const imgResponse = await axiosInstance.get(
                            `/ciem/portada/${data.code}`,
                            { responseType: 'blob' }
                        );
                        if (imgResponse.data && imgResponse.data.size > 0) {
                            const blobUrl = URL.createObjectURL(imgResponse.data);
                            setSavedBlobUrl(blobUrl);
                        }
                    } catch (imgErr) {
                        // No hay portada aún, es normal
                        setSavedBlobUrl(null);
                    }
                }
            } catch (error) {
                console.error('Error cargando producto:', error);
                setErrorMsg('No se pudo cargar la información del producto.');
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [ciemId]);

    // Imagen a mostrar: nueva seleccionada > portada guardada
    const displayImage = previewUrl || savedBlobUrl;
    const savedImageUrl = null; // ya no se usa, reemplazado por savedBlobUrl

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
            setErrorMsg('Formato no válido. Use JPG, PNG, GIF, WEBP o SVG.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setErrorMsg('La imagen no debe superar 5 MB.');
            return;
        }
        setErrorMsg('');
        setSuccessMsg('');
        setSelectedFile(file);
        // Se actualiza inmediatamente la previsualización
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) handleFileChange({ target: { files: [file] } });
    };

    const handleRemoveSelected = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // POST /ciem/uploadPortada/{code}/{name}
    const handleUpload = async () => {
        if (!selectedFile || !product) return;
        setUploading(true);
        setErrorMsg('');
        setSuccessMsg('');
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            await axiosInstance.post(
                `/ciem/uploadPortada/${product.code}/${encodeURIComponent(selectedFile.name)}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            // Refrescar la portada desde GET /ciem/portada/{code}
            try {
                const imgResponse = await axiosInstance.get(
                    `/ciem/portada/${product.code}`,
                    { responseType: 'blob' }
                );
                if (imgResponse.data && imgResponse.data.size > 0) {
                    setSavedBlobUrl(URL.createObjectURL(imgResponse.data));
                }
            } catch (imgErr) {
                setSavedBlobUrl(previewUrl);
            }

            setSuccessMsg('Imagen guardada correctamente.');
            setSelectedFile(null);
            setPreviewUrl(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            console.error('Error guardando imagen:', error);
            setErrorMsg('Error al guardar la imagen. Intente nuevamente.');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="container-fluid py-3">
            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <h4 className="mb-0">
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        3.2 Representación gráfica del producto
                    </h4>
                    {product?.name && (
                        <small className="opacity-75">
                            Idea de negocio: <strong>{product.name}</strong>
                        </small>
                    )}
                </Card.Header>

                <Card.Body>
                    {successMsg && (
                        <Alert variant="success" onClose={() => setSuccessMsg('')} dismissible>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            {successMsg}
                        </Alert>
                    )}
                    {errorMsg && (
                        <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible>
                            {errorMsg}
                        </Alert>
                    )}

                    {/* PREVISUALIZACIÓN — siempre visible y prominente */}
                    <div className="mb-4">
                        <div className="d-flex align-items-center mb-2 gap-2">
                            <Form.Label className="fw-bold mb-0">Previsualización</Form.Label>
                            {previewUrl && (
                                <Badge bg="primary" style={{ fontSize: '0.75rem' }}>Nueva imagen seleccionada</Badge>
                            )}
                            {!previewUrl && savedImageUrl && (
                                <Badge bg="secondary" style={{ fontSize: '0.75rem' }}>Imagen guardada</Badge>
                            )}
                        </div>
                        <div
                            style={{
                                border: displayImage ? '2px solid #0948B3' : '2px dashed #dee2e6',
                                borderRadius: '12px',
                                minHeight: '300px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: displayImage ? '#fff' : '#fafafa',
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {displayImage ? (
                                <Image
                                    src={displayImage}
                                    alt="Representación gráfica del producto"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                    }}
                                />
                            ) : (
                                <div className="text-center text-muted p-4">
                                    <FontAwesomeIcon icon={faImage} size="4x" className="mb-3 opacity-25" />
                                    <p className="mb-0">No hay imagen de portada aún</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CONTROLES DE CARGA */}
                    <Row className="align-items-start">
                        <Col md={8}>
                            {/* Zona drag & drop */}
                            <div
                                onDrop={handleDrop}
                                onDragOver={(e) => e.preventDefault()}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                style={{
                                    border: '2px dashed #0948B3',
                                    borderRadius: '10px',
                                    padding: '24px 20px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: '#f8f9ff',
                                    transition: 'background 0.2s',
                                }}
                                onMouseOver={e => e.currentTarget.style.background = '#eef2ff'}
                                onMouseOut={e => e.currentTarget.style.background = '#f8f9ff'}
                            >
                                <FontAwesomeIcon icon={faUpload} size="lg" className="text-primary mb-2" />
                                <p className="mb-0 fw-semibold text-primary small">
                                    Haz clic o arrastra una imagen aquí
                                </p>
                                <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                                    JPG, PNG, GIF, WEBP, SVG · Máx. 5 MB
                                </p>
                            </div>

                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />

                            {selectedFile && (
                                <div className="mt-2 d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        📎 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                                    </small>
                                    <Button variant="outline-danger" size="sm" onClick={handleRemoveSelected}>
                                        <FontAwesomeIcon icon={faTrash} className="me-1" /> Quitar
                                    </Button>
                                </div>
                            )}
                        </Col>

                        <Col md={4} className="d-flex align-items-end">
                            <Button
                                variant="primary"
                                disabled={!selectedFile || uploading}
                                onClick={handleUpload}
                                className="w-100"
                                style={{ minHeight: '80px' }}
                            >
                                {uploading ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Guardando...</>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} size="lg" className="d-block mx-auto mb-1" />
                                        Guardar imagen
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default RepresentacionGraficaPage;
